import { useState, useEffect } from 'react';

interface Pilot {
  rank: number;
  callsign: string;
  score: number;
  badge: string;
  tier: string;
  flights: number;
  avatar: string;
  specialty: string;
  streak: number;
  isCurrentUser?: boolean;
  joinedDate: string;
  aircraft: string;
  homeBase: string;
  totalHours: number;
  skills: { label: string; value: number; color: string }[];
  recentFlights: { date: string; score: number; route: string; duration: string; highlight: string }[];
  achievements: { icon: string; label: string }[];
}

export default function FeaturesHero() {
  const [activeMetric, setActiveMetric] = useState(0);
  const [flightProgress, setFlightProgress] = useState(0);
  const [hoveredPilot, setHoveredPilot] = useState<number | null>(null);
  const [selectedPilot, setSelectedPilot] = useState<number | null>(null);

  const metrics = [
    { label: 'Landing Score', value: 92, color: '#00FFD1' },
    { label: 'Airspeed Stability', value: 95, color: '#B4FF39' },
    { label: 'Radio Discipline', value: 88, color: '#00A3E0' },
    { label: 'Pattern Geometry', value: 91, color: '#00FFD1' }
  ];

  const leaderboard: Pilot[] = [
    {
      rank: 1, callsign: 'Maverick', score: 98, badge: 'gold', tier: 'ACE', flights: 312, avatar: 'M',
      specialty: 'Precision', streak: 14, joinedDate: 'Jan 2023', aircraft: 'Cessna 172S', homeBase: 'KVNY',
      totalHours: 847,
      skills: [
        { label: 'Approach Stability', value: 99, color: '#FFD700' },
        { label: 'Airspeed Control', value: 98, color: '#FFD700' },
        { label: 'Radio Clarity', value: 97, color: '#B4FF39' },
        { label: 'Checklist Discipline', value: 99, color: '#FFD700' },
        { label: 'Energy Management', value: 96, color: '#B4FF39' },
      ],
      recentFlights: [
        { date: 'Jun 12', score: 99, route: 'KVNY → KBUR', duration: '0:42', highlight: 'Perfect approach' },
        { date: 'Jun 10', score: 98, route: 'KBUR → KVNY', duration: '0:38', highlight: 'Smooth crosswind' },
        { date: 'Jun 7', score: 97, route: 'KVNY → KSMO', duration: '0:25', highlight: 'Greaser landing' },
        { date: 'Jun 4', score: 98, route: 'KSMO → KVNY', duration: '0:28', highlight: 'Flawless radio' },
      ],
      achievements: [
        { icon: 'ri-vip-crown-fill', label: '#1 Ranked' },
        { icon: 'ri-fire-fill', label: '14-Day Streak' },
        { icon: 'ri-star-fill', label: 'ACE Tier' },
        { icon: 'ri-shield-check-fill', label: '300+ Flights' },
      ]
    },
    {
      rank: 2, callsign: 'Iceman', score: 97, badge: 'silver', tier: 'ACE', flights: 289, avatar: 'I',
      specialty: 'Stability', streak: 11, joinedDate: 'Mar 2023', aircraft: 'Piper PA-28', homeBase: 'KSNA',
      totalHours: 712,
      skills: [
        { label: 'Approach Stability', value: 99, color: '#FFD700' },
        { label: 'Airspeed Control', value: 97, color: '#B4FF39' },
        { label: 'Radio Clarity', value: 95, color: '#B4FF39' },
        { label: 'Checklist Discipline', value: 98, color: '#FFD700' },
        { label: 'Energy Management', value: 97, color: '#B4FF39' },
      ],
      recentFlights: [
        { date: 'Jun 13', score: 98, route: 'KSNA → KFUL', duration: '0:31', highlight: 'Stable ILS' },
        { date: 'Jun 11', score: 97, route: 'KFUL → KSNA', duration: '0:29', highlight: 'On-speed approach' },
        { date: 'Jun 8', score: 96, route: 'KSNA → KLGB', duration: '0:22', highlight: 'Clean pattern' },
        { date: 'Jun 5', score: 97, route: 'KLGB → KSNA', duration: '0:24', highlight: 'Textbook final' },
      ],
      achievements: [
        { icon: 'ri-medal-fill', label: '#2 Ranked' },
        { icon: 'ri-fire-fill', label: '11-Day Streak' },
        { icon: 'ri-star-fill', label: 'ACE Tier' },
        { icon: 'ri-bar-chart-fill', label: 'Stability King' },
      ]
    },
    {
      rank: 3, callsign: 'Goose', score: 96, badge: 'bronze', tier: 'ACE', flights: 274, avatar: 'G',
      specialty: 'Radio', streak: 9, joinedDate: 'Feb 2023', aircraft: 'Cessna 172N', homeBase: 'KPOC',
      totalHours: 634,
      skills: [
        { label: 'Approach Stability', value: 95, color: '#B4FF39' },
        { label: 'Airspeed Control', value: 94, color: '#B4FF39' },
        { label: 'Radio Clarity', value: 99, color: '#FFD700' },
        { label: 'Checklist Discipline', value: 96, color: '#B4FF39' },
        { label: 'Energy Management', value: 95, color: '#B4FF39' },
      ],
      recentFlights: [
        { date: 'Jun 12', score: 97, route: 'KPOC → KONT', duration: '0:18', highlight: 'Perfect comms' },
        { date: 'Jun 9', score: 96, route: 'KONT → KPOC', duration: '0:20', highlight: 'Clear readbacks' },
        { date: 'Jun 6', score: 95, route: 'KPOC → KCNO', duration: '0:15', highlight: 'Smooth radio' },
        { date: 'Jun 3', score: 96, route: 'KCNO → KPOC', duration: '0:17', highlight: 'ATC praised' },
      ],
      achievements: [
        { icon: 'ri-award-fill', label: '#3 Ranked' },
        { icon: 'ri-fire-fill', label: '9-Day Streak' },
        { icon: 'ri-star-fill', label: 'ACE Tier' },
        { icon: 'ri-mic-fill', label: 'Radio Master' },
      ]
    },
    {
      rank: 4, callsign: 'Viper', score: 95, badge: 'default', tier: 'ELITE', flights: 201, avatar: 'V',
      specialty: 'Pattern', streak: 7, joinedDate: 'May 2023', aircraft: 'Diamond DA40', homeBase: 'KWHP',
      totalHours: 489,
      skills: [
        { label: 'Approach Stability', value: 96, color: '#00FFD1' },
        { label: 'Airspeed Control', value: 94, color: '#B4FF39' },
        { label: 'Radio Clarity', value: 93, color: '#B4FF39' },
        { label: 'Checklist Discipline', value: 95, color: '#00FFD1' },
        { label: 'Energy Management', value: 97, color: '#00FFD1' },
      ],
      recentFlights: [
        { date: 'Jun 13', score: 96, route: 'KWHP → KVNY', duration: '0:22', highlight: 'Tight pattern' },
        { date: 'Jun 10', score: 95, route: 'KVNY → KWHP', duration: '0:20', highlight: 'Perfect geometry' },
        { date: 'Jun 7', score: 94, route: 'KWHP → KBUR', duration: '0:28', highlight: 'Consistent legs' },
        { date: 'Jun 4', score: 95, route: 'KBUR → KWHP', duration: '0:25', highlight: 'Spot landing' },
      ],
      achievements: [
        { icon: 'ri-shield-star-line', label: 'ELITE Tier' },
        { icon: 'ri-fire-fill', label: '7-Day Streak' },
        { icon: 'ri-route-fill', label: 'Pattern Pro' },
        { icon: 'ri-flight-takeoff-fill', label: '200+ Flights' },
      ]
    },
    {
      rank: 5, callsign: 'Slider', score: 94, badge: 'default', tier: 'ELITE', flights: 188, avatar: 'S',
      specialty: 'Approach', streak: 6, isCurrentUser: true, joinedDate: 'Jun 2023', aircraft: 'Cessna 172SP', homeBase: 'KSMO',
      totalHours: 412,
      skills: [
        { label: 'Approach Stability', value: 97, color: '#00FFD1' },
        { label: 'Airspeed Control', value: 93, color: '#B4FF39' },
        { label: 'Radio Clarity', value: 91, color: '#B4FF39' },
        { label: 'Checklist Discipline', value: 94, color: '#00FFD1' },
        { label: 'Energy Management', value: 92, color: '#B4FF39' },
      ],
      recentFlights: [
        { date: 'Jun 14', score: 95, route: 'KSMO → KVNY', duration: '0:19', highlight: 'Best approach yet' },
        { date: 'Jun 11', score: 94, route: 'KVNY → KSMO', duration: '0:21', highlight: 'Smooth final' },
        { date: 'Jun 8', score: 93, route: 'KSMO → KLAX', duration: '0:35', highlight: 'Class B entry' },
        { date: 'Jun 5', score: 94, route: 'KLAX → KSMO', duration: '0:32', highlight: 'Stable ILS' },
      ],
      achievements: [
        { icon: 'ri-shield-star-line', label: 'ELITE Tier' },
        { icon: 'ri-fire-fill', label: '6-Day Streak' },
        { icon: 'ri-arrow-up-circle-fill', label: 'Rising Fast' },
        { icon: 'ri-user-star-fill', label: 'You!' },
      ]
    },
    {
      rank: 6, callsign: 'Jester', score: 93, badge: 'default', tier: 'ELITE', flights: 165, avatar: 'J',
      specialty: 'Energy', streak: 5, joinedDate: 'Jul 2023', aircraft: 'Piper PA-28-161', homeBase: 'KFUL',
      totalHours: 378,
      skills: [
        { label: 'Approach Stability', value: 92, color: '#B4FF39' },
        { label: 'Airspeed Control', value: 94, color: '#B4FF39' },
        { label: 'Radio Clarity', value: 91, color: '#B4FF39' },
        { label: 'Checklist Discipline', value: 93, color: '#00FFD1' },
        { label: 'Energy Management', value: 96, color: '#00FFD1' },
      ],
      recentFlights: [
        { date: 'Jun 13', score: 94, route: 'KFUL → KSNA', duration: '0:27', highlight: 'Energy managed' },
        { date: 'Jun 10', score: 93, route: 'KSNA → KFUL', duration: '0:29', highlight: 'Smooth descent' },
        { date: 'Jun 7', score: 92, route: 'KFUL → KLGB', duration: '0:20', highlight: 'Good power mgmt' },
        { date: 'Jun 4', score: 93, route: 'KLGB → KFUL', duration: '0:22', highlight: 'Consistent speed' },
      ],
      achievements: [
        { icon: 'ri-shield-star-line', label: 'ELITE Tier' },
        { icon: 'ri-fire-fill', label: '5-Day Streak' },
        { icon: 'ri-battery-fill', label: 'Energy Expert' },
        { icon: 'ri-flight-land-fill', label: 'Smooth Lander' },
      ]
    },
    {
      rank: 7, callsign: 'Cougar', score: 92, badge: 'default', tier: 'PRO', flights: 143, avatar: 'C',
      specialty: 'Checklist', streak: 4, joinedDate: 'Aug 2023', aircraft: 'Cessna 152', homeBase: 'KEMT',
      totalHours: 298,
      skills: [
        { label: 'Approach Stability', value: 91, color: '#00A3E0' },
        { label: 'Airspeed Control', value: 90, color: '#00A3E0' },
        { label: 'Radio Clarity', value: 89, color: '#00A3E0' },
        { label: 'Checklist Discipline', value: 97, color: '#00FFD1' },
        { label: 'Energy Management', value: 91, color: '#00A3E0' },
      ],
      recentFlights: [
        { date: 'Jun 12', score: 93, route: 'KEMT → KPOC', duration: '0:24', highlight: 'Zero checklist miss' },
        { date: 'Jun 9', score: 92, route: 'KPOC → KEMT', duration: '0:22', highlight: 'Perfect discipline' },
        { date: 'Jun 6', score: 91, route: 'KEMT → KWHP', duration: '0:18', highlight: 'All items checked' },
        { date: 'Jun 3', score: 92, route: 'KWHP → KEMT', duration: '0:20', highlight: 'Methodical preflight' },
      ],
      achievements: [
        { icon: 'ri-award-line', label: 'PRO Tier' },
        { icon: 'ri-list-check-3', label: 'Checklist King' },
        { icon: 'ri-fire-fill', label: '4-Day Streak' },
        { icon: 'ri-shield-check-line', label: 'Safety First' },
      ]
    },
    {
      rank: 8, callsign: 'Merlin', score: 91, badge: 'default', tier: 'PRO', flights: 128, avatar: 'M',
      specialty: 'Airspeed', streak: 3, joinedDate: 'Sep 2023', aircraft: 'Piper PA-28-181', homeBase: 'KCNO',
      totalHours: 267,
      skills: [
        { label: 'Approach Stability', value: 90, color: '#00A3E0' },
        { label: 'Airspeed Control', value: 96, color: '#00FFD1' },
        { label: 'Radio Clarity', value: 88, color: '#00A3E0' },
        { label: 'Checklist Discipline', value: 91, color: '#00A3E0' },
        { label: 'Energy Management', value: 90, color: '#00A3E0' },
      ],
      recentFlights: [
        { date: 'Jun 11', score: 92, route: 'KCNO → KONT', duration: '0:14', highlight: 'On-speed all the way' },
        { date: 'Jun 8', score: 91, route: 'KONT → KCNO', duration: '0:16', highlight: 'Vref nailed' },
        { date: 'Jun 5', score: 90, route: 'KCNO → KPOC', duration: '0:19', highlight: 'Consistent Vapp' },
        { date: 'Jun 2', score: 91, route: 'KPOC → KCNO', duration: '0:21', highlight: 'Speed discipline' },
      ],
      achievements: [
        { icon: 'ri-award-line', label: 'PRO Tier' },
        { icon: 'ri-speed-fill', label: 'Speed Wizard' },
        { icon: 'ri-fire-fill', label: '3-Day Streak' },
        { icon: 'ri-line-chart-fill', label: 'Improving Fast' },
      ]
    },
    {
      rank: 9, callsign: 'Wolfman', score: 90, badge: 'default', tier: 'PRO', flights: 112, avatar: 'W',
      specialty: 'Landing', streak: 2, joinedDate: 'Oct 2023', aircraft: 'Cessna 172M', homeBase: 'KLGB',
      totalHours: 231,
      skills: [
        { label: 'Approach Stability', value: 91, color: '#00A3E0' },
        { label: 'Airspeed Control', value: 89, color: '#00A3E0' },
        { label: 'Radio Clarity', value: 87, color: '#00A3E0' },
        { label: 'Checklist Discipline', value: 90, color: '#00A3E0' },
        { label: 'Energy Management', value: 91, color: '#00A3E0' },
      ],
      recentFlights: [
        { date: 'Jun 13', score: 91, route: 'KLGB → KSNA', duration: '0:26', highlight: 'Greaser touchdown' },
        { date: 'Jun 10', score: 90, route: 'KSNA → KLGB', duration: '0:28', highlight: 'Smooth flare' },
        { date: 'Jun 7', score: 89, route: 'KLGB → KFUL', duration: '0:22', highlight: 'Centerline hold' },
        { date: 'Jun 4', score: 90, route: 'KFUL → KLGB', duration: '0:20', highlight: 'Safety conscious' },
      ],
      achievements: [
        { icon: 'ri-award-line', label: 'PRO Tier' },
        { icon: 'ri-flight-land-fill', label: 'Landing Ace' },
        { icon: 'ri-fire-fill', label: '2-Day Streak' },
        { icon: 'ri-map-pin-fill', label: 'KLGB Local' },
      ]
    },
    {
      rank: 10, callsign: 'Sundown', score: 89, badge: 'default', tier: 'PRO', flights: 98, avatar: 'S',
      specialty: 'Safety', streak: 1, joinedDate: 'Nov 2023', aircraft: 'Cessna 172R', homeBase: 'KBUR',
      totalHours: 198,
      skills: [
        { label: 'Approach Stability', value: 88, color: '#00A3E0' },
        { label: 'Airspeed Control', value: 87, color: '#00A3E0' },
        { label: 'Radio Clarity', value: 89, color: '#00A3E0' },
        { label: 'Checklist Discipline', value: 92, color: '#00FFD1' },
        { label: 'Energy Management', value: 88, color: '#00A3E0' },
      ],
      recentFlights: [
        { date: 'Jun 14', score: 90, route: 'KBUR → KVNY', duration: '0:17', highlight: 'Safe & steady' },
        { date: 'Jun 11', score: 89, route: 'KVNY → KBUR', duration: '0:19', highlight: 'No deviations' },
        { date: 'Jun 8', score: 88, route: 'KBUR → KWHP', duration: '0:22', highlight: 'Thorough preflight' },
        { date: 'Jun 5', score: 89, route: 'KWHP → KBUR', duration: '0:20', highlight: 'Safety conscious' },
      ],
      achievements: [
        { icon: 'ri-award-line', label: 'PRO Tier' },
        { icon: 'ri-shield-fill', label: 'Safety First' },
        { icon: 'ri-fire-line', label: '1-Day Streak' },
        { icon: 'ri-user-add-fill', label: 'New Climber' },
      ]
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMetric((prev) => (prev + 1) % metrics.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFlightProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'bronze': return '#CD7F32';
      default: return '#00A3E0';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'ACE': return { bg: 'rgba(255,215,0,0.15)', text: '#FFD700', border: 'rgba(255,215,0,0.4)' };
      case 'ELITE': return { bg: 'rgba(0,255,209,0.12)', text: '#00FFD1', border: 'rgba(0,255,209,0.35)' };
      case 'PRO': return { bg: 'rgba(0,163,224,0.12)', text: '#00A3E0', border: 'rgba(0,163,224,0.35)' };
      default: return { bg: 'rgba(107,114,128,0.1)', text: '#6B7280', border: 'rgba(107,114,128,0.3)' };
    }
  };

  const getAvatarGradient = (rank: number, badge: string) => {
    if (badge === 'gold') return 'from-yellow-400 to-yellow-600';
    if (badge === 'silver') return 'from-gray-300 to-gray-500';
    if (badge === 'bronze') return 'from-orange-400 to-orange-700';
    if (rank <= 6) return 'from-cyan-500 to-teal-700';
    return 'from-blue-500 to-blue-800';
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return '#FFD700';
    if (score >= 90) return '#00FFD1';
    return '#00A3E0';
  };

  const handlePilotClick = (rank: number) => {
    setSelectedPilot(selectedPilot === rank ? null : rank);
  };

  const selectedPilotData = leaderboard.find(p => p.rank === selectedPilot);

  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      {/* ── BACKGROUND: Cockpit environment ── */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f56f521b37373e096bb9d14b27cd4602.jpg"
          alt="Cockpit view on approach"
          className="w-full h-full object-cover object-top"
        />
        {/* Deep navy overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020D1F]/85 via-[#020D1F]/75 to-[#020D1F]/95"></div>
        {/* Subtle vignette */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(2,13,31,0.7) 100%)' }}></div>
      </div>

      {/* ── AVIONICS SCAN-LINE TEXTURE ── */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,200,255,0.8) 2px, rgba(0,200,255,0.8) 3px)', backgroundSize: '100% 4px' }}>
      </div>

      {/* ── FLOATING TELEMETRY OVERLAYS ── */}
      {/* Top-left: HDG */}
      <div className="absolute top-28 left-6 sm:left-10 pointer-events-none z-20 hidden md:block">
        <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#00C8FF]/30 rounded px-3 py-1.5 backdrop-blur-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-[#00C8FF] animate-pulse"></div>
          <span className="text-[#00C8FF] font-mono text-xs tracking-widest font-semibold">HDG 274°</span>
        </div>
      </div>
      {/* Top-right: ALT */}
      <div className="absolute top-28 right-6 sm:right-10 pointer-events-none z-20 hidden md:block">
        <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#B4FF39]/30 rounded px-3 py-1.5 backdrop-blur-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-[#B4FF39] animate-pulse"></div>
          <span className="text-[#B4FF39] font-mono text-xs tracking-widest font-semibold">ALT 3000 FT</span>
        </div>
      </div>
      {/* Mid-left: WIND */}
      <div className="absolute top-1/2 -translate-y-1/2 left-4 sm:left-8 pointer-events-none z-20 hidden lg:block">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#00FFD1]/25 rounded px-3 py-1.5 backdrop-blur-sm">
            <i className="ri-windy-line text-[#00FFD1] text-xs"></i>
            <span className="text-[#00FFD1] font-mono text-xs tracking-widest font-semibold">WIND 240° / 12 KT</span>
          </div>
          <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#00C8FF]/20 rounded px-3 py-1.5 backdrop-blur-sm">
            <i className="ri-speed-line text-[#00C8FF] text-xs"></i>
            <span className="text-[#00C8FF] font-mono text-xs tracking-widest font-semibold">GS 118 KT</span>
          </div>
        </div>
      </div>
      {/* Mid-right: KLNS RWY */}
      <div className="absolute top-1/2 -translate-y-1/2 right-4 sm:right-8 pointer-events-none z-20 hidden lg:block">
        <div className="flex flex-col gap-1.5 items-end">
          <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#FFD700]/30 rounded px-3 py-1.5 backdrop-blur-sm">
            <i className="ri-flight-land-line text-[#FFD700] text-xs"></i>
            <span className="text-[#FFD700] font-mono text-xs tracking-widest font-semibold">KLNS RWY 26</span>
          </div>
          <div className="flex items-center gap-2 bg-[#020D1F]/70 border border-[#00C8FF]/20 rounded px-3 py-1.5 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-[#00C8FF] animate-pulse"></div>
            <span className="text-[#00C8FF] font-mono text-xs tracking-widest font-semibold">APCH STABLE</span>
          </div>
        </div>
      </div>

      {/* ── THIN GLOWING CORNER BRACKETS (avionics HUD style) ── */}
      <div className="absolute top-24 left-4 w-12 h-12 pointer-events-none z-10 hidden sm:block">
        <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-[#00C8FF]/40 rounded-tl"></div>
      </div>
      <div className="absolute top-24 right-4 w-12 h-12 pointer-events-none z-10 hidden sm:block">
        <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-[#00C8FF]/40 rounded-tr"></div>
      </div>
      <div className="absolute bottom-4 left-4 w-12 h-12 pointer-events-none z-10 hidden sm:block">
        <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-[#00C8FF]/40 rounded-bl"></div>
      </div>
      <div className="absolute bottom-4 right-4 w-12 h-12 pointer-events-none z-10 hidden sm:block">
        <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-[#00C8FF]/40 rounded-br"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full">
        {/* Hero Text */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 tracking-tight">WingmanFix</h1>
          <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-[#00FFD1] font-semibold mb-4 sm:mb-6 px-4">
            The copilot that keeps you ahead of the airplane.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-3 sm:mb-4 leading-relaxed px-4">
            As easy as talking to the right seat.<br />
            No extra screens. No extra workload. Just ask.
          </p>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-4 italic">
            WingmanFix helps in the moment and turns every flight into a complete post-flight debrief.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center px-4">
            <button className="bg-[#00A3E0] hover:bg-[#0088BB] text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg transition-all font-semibold text-base sm:text-lg shadow-lg hover:shadow-[#00A3E0]/50 hover:scale-105 cursor-pointer whitespace-nowrap">
              Download the App
            </button>
            <button className="border-2 border-[#00FFD1] text-[#00FFD1] hover:bg-[#00FFD1]/10 px-8 sm:px-10 py-3 sm:py-4 rounded-lg transition-all font-semibold text-base sm:text-lg cursor-pointer whitespace-nowrap">
              See How It Works
            </button>
          </div>
        </div>

        {/* Three Column Layout */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">

          {/* ════════════════════════════════════════════════════
              LEFT: GLASS COCKPIT VISUAL — replaces old dashboard
              ════════════════════════════════════════════════════ */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden border border-[#00C8FF]/25 shadow-2xl shadow-[#00C8FF]/10"
            style={{ background: 'linear-gradient(160deg, #020D1F 0%, #041428 60%, #061830 100%)' }}>

            {/* Subtle inner glow */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,200,255,0.07) 0%, transparent 65%)' }}></div>

            {/* ── PANEL HEADER ── */}
            <div className="relative flex items-center justify-between px-5 sm:px-7 pt-5 pb-3 border-b border-[#00C8FF]/15">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00FFD1] animate-pulse shadow-[0_0_8px_#00FFD1]"></div>
                <span className="text-[#00C8FF] font-mono text-xs sm:text-sm tracking-[0.2em] font-semibold uppercase">WingmanFix · Cockpit AI</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[#FFD700] font-mono text-xs tracking-widest">KLNS · RWY 26</span>
                <div className="flex gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00FFD1]/60"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FFD700]/60"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400/60"></div>
                </div>
              </div>
            </div>

            {/* ── MAIN CONTENT AREA ── */}
            <div className="p-4 sm:p-6 md:p-7">

              {/* ── TOP ROW: Cockpit image + Device mockup ── */}
              <div className="grid md:grid-cols-5 gap-4 sm:gap-5 mb-5">

                {/* Cockpit window view */}
                <div className="md:col-span-3 relative rounded-xl overflow-hidden border border-[#00C8FF]/20"
                  style={{ minHeight: '220px' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Cessna%20172%20cockpit%20windshield%20view%20on%20final%20approach%20to%20runway%20at%20golden%20hour%20warm%20amber%20sunset%20light%20runway%20centerline%20visible%20ahead%20instrument%20panel%20silhouette%20in%20foreground%20realistic%20aviation%20photography%20dramatic%20lighting%20sky%20orange%20and%20gold&width=800&height=500&seq=cockpit-windshield-runway-final-002&orientation=landscape"
                    alt="Cockpit approach view"
                    className="w-full h-full object-cover object-top"
                    style={{ minHeight: '220px' }}
                  />
                  {/* Dark overlay for contrast */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020D1F]/80 via-transparent to-[#020D1F]/30"></div>

                  {/* GPS Traffic Pattern SVG overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <svg className="w-full h-full" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
                      {/* Faint runway */}
                      <rect x="188" y="160" width="24" height="80" fill="rgba(255,215,0,0.08)" rx="2" />
                      <line x1="200" y1="160" x2="200" y2="240" stroke="rgba(255,215,0,0.35)" strokeWidth="1.5" strokeDasharray="6,5" />
                      {/* Traffic pattern path — downwind, base, final */}
                      <path
                        d="M 200 230 L 310 228 L 312 80 L 90 78 L 88 228 L 188 230"
                        stroke="rgba(0,200,255,0.18)"
                        strokeWidth="1.5"
                        fill="none"
                        strokeDasharray="5,5"
                      />
                      {/* Animated flight path */}
                      <path
                        d="M 200 230 L 308 226 L 310 82 L 92 80 L 90 226 L 188 230"
                        stroke="#00C8FF"
                        strokeWidth="2.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          strokeDasharray: 1100,
                          strokeDashoffset: 1100 - (flightProgress * 11),
                          filter: 'drop-shadow(0 0 4px #00C8FF)',
                          transition: 'stroke-dashoffset 0.05s linear'
                        }}
                      />
                      {/* Aircraft position dot */}
                      <circle
                        cx={200 - (flightProgress * 1.1)}
                        cy={230 - (flightProgress * 0.4)}
                        r="5"
                        fill="#00FFD1"
                        style={{ filter: 'drop-shadow(0 0 6px #00FFD1)' }}
                      />
                      <circle
                        cx={200 - (flightProgress * 1.1)}
                        cy={230 - (flightProgress * 0.4)}
                        r="9"
                        fill="none"
                        stroke="#00FFD1"
                        strokeWidth="1"
                        opacity="0.4"
                      />
                      {/* Pattern labels */}
                      <text x="330" y="155" fill="rgba(0,200,255,0.7)" fontSize="9" fontFamily="monospace" fontWeight="bold">DOWNWIND</text>
                      <text x="155" y="68" fill="rgba(0,200,255,0.7)" fontSize="9" fontFamily="monospace" fontWeight="bold">BASE</text>
                      <text x="42" y="155" fill="rgba(0,200,255,0.7)" fontSize="9" fontFamily="monospace" fontWeight="bold">FINAL</text>
                      <text x="200" y="252" fill="rgba(255,215,0,0.8)" fontSize="9" fontFamily="monospace" fontWeight="bold" textAnchor="middle">RWY 26 · KLNS</text>
                      {/* Tick marks on path corners */}
                      <line x1="305" y1="75" x2="318" y2="75" stroke="rgba(0,200,255,0.5)" strokeWidth="1" />
                      <line x1="305" y1="75" x2="305" y2="88" stroke="rgba(0,200,255,0.5)" strokeWidth="1" />
                      <line x1="88" y1="75" x2="95" y2="75" stroke="rgba(0,200,255,0.5)" strokeWidth="1" />
                      <line x1="88" y1="75" x2="88" y2="88" stroke="rgba(0,200,255,0.5)" strokeWidth="1" />
                    </svg>
                  </div>

                  {/* Bottom telemetry strip */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2"
                    style={{ background: 'linear-gradient(to top, rgba(2,13,31,0.95), transparent)' }}>
                    <span className="text-[#00C8FF] font-mono text-xs tracking-widest">HDG 274°</span>
                    <span className="text-[#B4FF39] font-mono text-xs tracking-widest">ALT 3000 FT</span>
                    <span className="text-[#00C8FF] font-mono text-xs tracking-widest">GS 118 KT</span>
                    <span className="text-[#FFD700] font-mono text-xs tracking-widest">WIND 240°/12</span>
                  </div>
                </div>

                {/* Device mockup — WingmanFix dashboard */}
                <div className="md:col-span-2 flex flex-col gap-3">
                  {/* Device frame */}
                  <div className="relative rounded-2xl border-2 border-[#00C8FF]/30 overflow-hidden flex-1"
                    style={{ background: 'linear-gradient(160deg, #030F22, #051525)', minHeight: '200px', boxShadow: '0 0 30px rgba(0,200,255,0.12), inset 0 0 20px rgba(0,200,255,0.04)' }}>

                    {/* Device notch */}
                    <div className="flex justify-center pt-2 pb-1">
                      <div className="w-16 h-1.5 rounded-full bg-[#00C8FF]/20"></div>
                    </div>

                    {/* App header */}
                    <div className="flex items-center justify-between px-3 pb-2 border-b border-[#00C8FF]/15">
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-flight-takeoff-fill text-[#00FFD1] text-xs"></i>
                        </div>
                        <span className="text-[#00FFD1] font-mono text-xs font-bold tracking-wider">WINGMANFIX</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] animate-pulse"></div>
                        <span className="text-[#00FFD1] font-mono text-xs">LIVE</span>
                      </div>
                    </div>

                    {/* Flight score ring */}
                    <div className="flex flex-col items-center py-3">
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 96 96">
                          {/* Tick marks around ring */}
                          {Array.from({ length: 24 }).map((_, i) => {
                            const angle = (i / 24) * 360;
                            const rad = (angle * Math.PI) / 180;
                            const r1 = 44, r2 = i % 6 === 0 ? 38 : 41;
                            return (
                              <line
                                key={i}
                                x1={48 + r1 * Math.cos(rad)}
                                y1={48 + r1 * Math.sin(rad)}
                                x2={48 + r2 * Math.cos(rad)}
                                y2={48 + r2 * Math.sin(rad)}
                                stroke="rgba(0,200,255,0.25)"
                                strokeWidth="1"
                              />
                            );
                          })}
                          <circle cx="48" cy="48" r="38" stroke="#0A1E35" strokeWidth="7" fill="none" />
                          <circle cx="48" cy="48" r="38" stroke="#00FFD1" strokeWidth="7" fill="none"
                            strokeDasharray="238.76" strokeDashoffset="17.9"
                            style={{ filter: 'drop-shadow(0 0 6px #00FFD1)' }} />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-2xl sm:text-3xl font-extrabold text-white leading-none">92</span>
                          <span className="text-xs text-[#00FFD1] font-mono tracking-wider">SCORE</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 font-mono mt-1 tracking-widest">LANDING SCORE</span>
                    </div>

                    {/* Four metrics */}
                    <div className="px-3 pb-3 space-y-2">
                      {metrics.map((metric, idx) => (
                        <div key={metric.label}
                          className={`rounded-lg px-2.5 py-1.5 border transition-all duration-300 ${activeMetric === idx ? 'border-[#00C8FF]/50 shadow-[0_0_8px_rgba(0,200,255,0.2)]' : 'border-[#00C8FF]/10'}`}
                          style={{ background: activeMetric === idx ? 'rgba(0,200,255,0.08)' : 'rgba(5,20,40,0.6)' }}
                          onClick={() => setActiveMetric(idx)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-400 font-mono truncate">{metric.label}</span>
                            <span className="text-xs font-extrabold font-mono ml-1" style={{ color: metric.color }}>{metric.value}</span>
                          </div>
                          <div className="w-full h-1 rounded-full bg-[#0A1E35] overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${metric.value}%`, backgroundColor: metric.color, boxShadow: `0 0 4px ${metric.color}` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── BOTTOM ROW: Avionics-style instrument strip ── */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">

                {/* Airspeed tape */}
                <div className="rounded-xl border border-[#00C8FF]/20 overflow-hidden"
                  style={{ background: 'linear-gradient(160deg, #030F22, #041525)' }}>
                  <div className="px-3 pt-2.5 pb-1 border-b border-[#00C8FF]/15">
                    <span className="text-[#00C8FF] font-mono text-xs tracking-widest font-semibold">AIRSPEED</span>
                  </div>
                  <div className="px-3 py-2 space-y-0.5">
                    {[125, 120, 115, 110, 105].map((speed, idx) => (
                      <div key={speed} className={`text-right font-mono transition-all ${idx === 2 ? 'text-[#00FFD1] text-2xl font-extrabold' : 'text-gray-600 text-sm'}`}
                        style={idx === 2 ? { textShadow: '0 0 10px rgba(0,255,209,0.6)' } : {}}>
                        {speed}
                      </div>
                    ))}
                    <div className="text-right text-xs text-gray-600 font-mono mt-1">KIAS</div>
                  </div>
                  {/* Tick marks */}
                  <div className="flex justify-end px-2 pb-2 gap-0.5">
                    {[1,2,3,4,5,6,7,8].map(i => (
                      <div key={i} className="w-px rounded-full bg-[#00C8FF]/20" style={{ height: i % 4 === 0 ? '8px' : '4px' }}></div>
                    ))}
                  </div>
                </div>

                {/* Altitude tape */}
                <div className="rounded-xl border border-[#B4FF39]/20 overflow-hidden"
                  style={{ background: 'linear-gradient(160deg, #030F22, #041525)' }}>
                  <div className="px-3 pt-2.5 pb-1 border-b border-[#B4FF39]/15">
                    <span className="text-[#B4FF39] font-mono text-xs tracking-widest font-semibold">ALTITUDE</span>
                  </div>
                  <div className="px-3 py-2 space-y-0.5">
                    {[3200, 3100, 3000, 2900, 2800].map((alt, idx) => (
                      <div key={alt} className={`text-right font-mono transition-all ${idx === 2 ? 'text-[#B4FF39] text-2xl font-extrabold' : 'text-gray-600 text-sm'}`}
                        style={idx === 2 ? { textShadow: '0 0 10px rgba(180,255,57,0.6)' } : {}}>
                        {alt}
                      </div>
                    ))}
                    <div className="text-right text-xs text-gray-600 font-mono mt-1">FT MSL</div>
                  </div>
                  <div className="flex justify-end px-2 pb-2 gap-0.5">
                    {[1,2,3,4,5,6,7,8].map(i => (
                      <div key={i} className="w-px rounded-full bg-[#B4FF39]/20" style={{ height: i % 4 === 0 ? '8px' : '4px' }}></div>
                    ))}
                  </div>
                </div>

                {/* Approach stability gauge */}
                <div className="rounded-xl border border-[#00FFD1]/20 overflow-hidden flex flex-col items-center justify-center py-3"
                  style={{ background: 'linear-gradient(160deg, #030F22, #041525)' }}>
                  <span className="text-[#00FFD1] font-mono text-xs tracking-widest font-semibold mb-2">APPROACH</span>
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                      {Array.from({ length: 20 }).map((_, i) => {
                        const angle = (i / 20) * 360;
                        const rad = (angle * Math.PI) / 180;
                        const r1 = 36, r2 = i % 5 === 0 ? 30 : 33;
                        return (
                          <line key={i}
                            x1={40 + r1 * Math.cos(rad)} y1={40 + r1 * Math.sin(rad)}
                            x2={40 + r2 * Math.cos(rad)} y2={40 + r2 * Math.sin(rad)}
                            stroke="rgba(0,255,209,0.2)" strokeWidth="1" />
                        );
                      })}
                      <circle cx="40" cy="40" r="28" stroke="#0A1E35" strokeWidth="6" fill="none" />
                      <circle cx="40" cy="40" r="28" stroke="#00FFD1" strokeWidth="6" fill="none"
                        strokeDasharray="175.9" strokeDashoffset="26.4"
                        style={{ filter: 'drop-shadow(0 0 5px #00FFD1)' }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl sm:text-2xl font-extrabold text-white leading-none">85</span>
                      <span className="text-xs text-[#00FFD1] font-mono" style={{ fontSize: '9px' }}>STABLE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ════ END LEFT PANEL ════ */}

          {/* RIGHT: Wingman Buddies Leaderboard — unchanged */}
          <div className="bg-[#0D1B2E]/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-[#00FFD1]/20 shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-yellow-500/10 rounded-lg border border-yellow-500/30 flex-shrink-0">
                <i className="ri-trophy-fill text-lg sm:text-xl text-[#FFD700]"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-white">Wingman Buddies</h3>
                <p className="text-xs text-gray-400 hidden sm:block">Click a pilot to view full profile</p>
              </div>
              <div className="ml-auto flex items-center gap-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-2 sm:px-3 py-1 flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse"></div>
                <span className="text-xs text-[#00FFD1] font-semibold">LIVE</span>
              </div>
            </div>

            {/* Tier Legend */}
            <div className="flex gap-2 mb-3 sm:mb-4 mt-1 flex-wrap">
              {['ACE', 'ELITE', 'PRO'].map((tier) => {
                const tc = getTierColor(tier);
                return (
                  <div key={tier} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border"
                    style={{ background: tc.bg, color: tc.text, borderColor: tc.border }}>
                    {tier === 'ACE' && <i className="ri-star-fill text-xs"></i>}
                    {tier === 'ELITE' && <i className="ri-shield-star-line text-xs"></i>}
                    {tier === 'PRO' && <i className="ri-award-line text-xs"></i>}
                    {tier}
                  </div>
                );
              })}
            </div>

            {/* Pilot List */}
            <div className="space-y-1.5 flex-1 overflow-y-auto max-h-[400px] lg:max-h-none">
              {leaderboard.map((pilot) => {
                const tierColors = getTierColor(pilot.tier);
                const isHovered = hoveredPilot === pilot.rank;
                const isSelected = selectedPilot === pilot.rank;
                return (
                  <div key={pilot.rank}>
                    <div
                      className={`relative flex items-center gap-2 sm:gap-3 p-2 sm:p-2.5 rounded-xl transition-all duration-200 cursor-pointer ${
                        pilot.isCurrentUser
                          ? 'border-2 border-[#00FFD1] shadow-lg shadow-[#00FFD1]/20'
                          : isSelected
                          ? 'border-2 border-[#00A3E0]/70'
                          : 'border border-gray-700/60 hover:border-gray-500'
                      }`}
                      style={{
                        background: isSelected
                          ? 'rgba(0,163,224,0.12)'
                          : pilot.isCurrentUser
                          ? 'linear-gradient(135deg, rgba(0,255,209,0.12), rgba(0,163,224,0.08))'
                          : isHovered
                          ? 'rgba(255,255,255,0.04)'
                          : 'rgba(10,22,40,0.8)'
                      }}
                      onMouseEnter={() => setHoveredPilot(pilot.rank)}
                      onMouseLeave={() => setHoveredPilot(null)}
                      onClick={() => handlePilotClick(pilot.rank)}
                    >
                      <div className="w-4 sm:w-5 text-center flex-shrink-0">
                        {getRankIcon(pilot.rank) ? (
                          <span className="text-sm sm:text-base leading-none">{getRankIcon(pilot.rank)}</span>
                        ) : (
                          <span className="text-xs font-bold text-gray-500">#{pilot.rank}</span>
                        )}
                      </div>
                      <div className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br ${getAvatarGradient(pilot.rank, pilot.badge)} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        <span className="text-xs sm:text-sm font-extrabold text-white drop-shadow">{pilot.avatar}</span>
                        {pilot.streak >= 6 && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-orange-500 rounded-full flex items-center justify-center shadow">
                            <i className="ri-fire-fill text-white" style={{ fontSize: '8px' }}></i>
                          </div>
                        )}
                        {pilot.rank === 1 && (
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                            <i className="ri-vip-crown-fill text-yellow-400" style={{ fontSize: '10px' }}></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <p className={`font-bold text-xs sm:text-sm truncate ${pilot.isCurrentUser ? 'text-[#00FFD1]' : isSelected ? 'text-[#00A3E0]' : 'text-white'}`}>
                            {pilot.callsign}
                          </p>
                          {pilot.isCurrentUser && (
                            <span className="text-xs bg-[#00FFD1]/20 text-[#00FFD1] px-1 sm:px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">YOU</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 sm:gap-1.5 mt-0.5">
                          <span className="text-xs font-bold px-1 sm:px-1.5 py-0 rounded border"
                            style={{ background: tierColors.bg, color: tierColors.text, borderColor: tierColors.border }}>
                            {pilot.tier}
                          </span>
                          <span className="text-xs text-gray-500 truncate hidden sm:inline">{pilot.specialty}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-1 sm:gap-2">
                        <div>
                          <div className="text-lg sm:text-xl font-extrabold leading-none" style={{ color: pilot.rank <= 3 ? getBadgeColor(pilot.badge) : pilot.isCurrentUser ? '#00FFD1' : '#00A3E0' }}>
                            {pilot.score}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center justify-end gap-0.5">
                            <i className="ri-flight-takeoff-line" style={{ fontSize: '9px' }}></i>
                            {pilot.flights}
                          </div>
                        </div>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-arrow-${isSelected ? 'up' : 'down'}-s-line text-gray-400 text-sm transition-transform duration-200`}></i>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 h-0.5 rounded-b-xl transition-all duration-300"
                        style={{
                          width: `${(pilot.streak / 14) * 100}%`,
                          background: pilot.isCurrentUser ? '#00FFD1' : pilot.rank <= 3 ? getBadgeColor(pilot.badge) : '#00A3E0',
                          opacity: 0.5
                        }}
                      ></div>
                    </div>

                    {isSelected && selectedPilotData && selectedPilotData.rank === pilot.rank && (
                      <div
                        className="mt-1 mb-1 rounded-xl border border-[#00A3E0]/40 overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, rgba(0,20,45,0.98), rgba(5,15,35,0.98))' }}
                      >
                        <div className="p-3 sm:p-4 border-b border-gray-700/50">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${getAvatarGradient(pilot.rank, pilot.badge)} flex items-center justify-center shadow-lg flex-shrink-0`}>
                              <span className="text-xl sm:text-2xl font-extrabold text-white">{pilot.avatar}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base sm:text-lg font-extrabold text-white">{pilot.callsign}</span>
                                {pilot.isCurrentUser && (
                                  <span className="text-xs bg-[#00FFD1]/20 text-[#00FFD1] px-2 py-0.5 rounded-full font-bold">YOU</span>
                                )}
                                <span className="text-xs font-bold px-2 py-0.5 rounded border"
                                  style={{ background: getTierColor(pilot.tier).bg, color: getTierColor(pilot.tier).text, borderColor: getTierColor(pilot.tier).border }}>
                                  {pilot.tier}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <i className="ri-plane-fill text-[#00A3E0]"></i>
                                  <span className="truncate">{pilot.aircraft}</span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-map-pin-fill text-[#00FFD1]"></i>
                                  {pilot.homeBase}
                                </span>
                                <span className="flex items-center gap-1 hidden sm:flex">
                                  <i className="ri-calendar-line text-gray-500"></i>
                                  Since {pilot.joinedDate}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { label: 'Score', value: pilot.score, color: getScoreColor(pilot.score), icon: 'ri-bar-chart-fill' },
                              { label: 'Flights', value: pilot.flights, color: '#00A3E0', icon: 'ri-flight-takeoff-fill' },
                              { label: 'Hours', value: `${pilot.totalHours}h`, color: '#B4FF39', icon: 'ri-time-fill' },
                            ].map((stat) => (
                              <div key={stat.label} className="bg-[#0A1628]/80 rounded-lg p-2 text-center border border-gray-700/50">
                                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center mx-auto mb-0.5">
                                  <i className={`${stat.icon} text-xs sm:text-sm`} style={{ color: stat.color }}></i>
                                </div>
                                <div className="text-sm sm:text-base font-extrabold" style={{ color: stat.color }}>{stat.value}</div>
                                <div className="text-xs text-gray-500">{stat.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-3 sm:p-4 border-b border-gray-700/50">
                          <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2 sm:mb-3 flex items-center gap-1.5">
                            <i className="ri-bar-chart-2-line text-[#00FFD1]"></i>
                            Skill Breakdown
                          </div>
                          <div className="space-y-2">
                            {pilot.skills.map((skill) => (
                              <div key={skill.label}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-gray-400 truncate">{skill.label}</span>
                                  <span className="text-xs font-bold ml-2" style={{ color: skill.color }}>{skill.value}</span>
                                </div>
                                <div className="w-full h-1.5 bg-[#1A2942] rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all duration-700"
                                    style={{ width: `${skill.value}%`, backgroundColor: skill.color }}></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-3 sm:p-4 border-b border-gray-700/50">
                          <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2 sm:mb-3 flex items-center gap-1.5">
                            <i className="ri-history-line text-[#00A3E0]"></i>
                            Recent Flights
                          </div>
                          <div className="space-y-2">
                            {pilot.recentFlights.map((flight, idx) => (
                              <div key={idx} className="flex items-center gap-2 bg-[#0A1628]/60 rounded-lg p-2 border border-gray-700/40">
                                <div className="text-xs text-gray-500 w-10 flex-shrink-0 font-mono">{flight.date}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-white truncate">{flight.route}</div>
                                  <div className="text-xs text-gray-500 truncate">{flight.highlight}</div>
                                </div>
                                <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                                  <span className="text-xs text-gray-500 font-mono hidden sm:inline">{flight.duration}</span>
                                  <span className="text-xs sm:text-sm font-extrabold" style={{ color: getScoreColor(flight.score) }}>{flight.score}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-3 sm:p-4">
                          <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2 sm:mb-3 flex items-center gap-1.5">
                            <i className="ri-medal-fill text-[#FFD700]"></i>
                            Achievements
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {pilot.achievements.map((ach, idx) => (
                              <div key={idx} className="flex items-center gap-2 bg-[#0A1628]/60 rounded-lg px-2 sm:px-2.5 py-2 border border-gray-700/40">
                                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0">
                                  <i className={`${ach.icon} text-xs sm:text-sm text-[#FFD700]`}></i>
                                </div>
                                <span className="text-xs text-gray-300 font-medium truncate">{ach.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer stats */}
            <div className="mt-4 pt-4 border-t border-gray-700/60">
              <div className="grid grid-cols-3 gap-2 mb-3 sm:mb-4">
                {[
                  { label: 'Pilots', value: '2,841', icon: 'ri-user-line' },
                  { label: 'Flights', value: '18.4K', icon: 'ri-flight-takeoff-line' },
                  { label: 'Avg Score', value: '87.2', icon: 'ri-bar-chart-line' }
                ].map((stat) => (
                  <div key={stat.label} className="bg-[#0A1628] rounded-lg p-2 text-center border border-gray-700/50">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} text-[#00A3E0] text-xs sm:text-sm`}></i>
                    </div>
                    <div className="text-xs sm:text-sm font-bold text-white">{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
              <button className="w-full bg-[#00A3E0] hover:bg-[#0088BB] text-white py-2 sm:py-3 rounded-lg transition-all font-semibold text-sm sm:text-base cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-95">
                View Full Rankings
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Performance Dashboard — unchanged */}
        <div className="mt-6 sm:mt-8 bg-[#0D1B2E]/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 border border-[#00FFD1]/20 shadow-2xl">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
            <div className="w-3 h-3 rounded-full bg-[#B4FF39]"></div>
            <h3 className="text-lg sm:text-xl font-bold text-white">Gamified Flight Scoring</h3>
            <span className="text-xs sm:text-sm text-gray-400 ml-auto">Last 8 Flights</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            {[
              { label: 'Approach Stability', value: 94, trend: '+3' },
              { label: 'Airspeed Control', value: 96, trend: '+2' },
              { label: 'Radio Clarity', value: 89, trend: '+5' },
              { label: 'Checklist Discipline', value: 91, trend: '+1' },
              { label: 'Energy Management', value: 93, trend: '+4' }
            ].map((skill) => (
              <div key={skill.label} className="bg-[#0A1628] rounded-xl p-3 sm:p-4 border border-gray-700">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider truncate">{skill.label}</div>
                <div className="flex items-end gap-1 sm:gap-2 mb-2 sm:mb-3">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#B4FF39]">{skill.value}</div>
                  <div className="text-xs sm:text-sm text-[#00FFD1] font-semibold mb-1">{skill.trend}</div>
                </div>
                <div className="w-full h-2 bg-[#1A2942] rounded-full overflow-hidden">
                  <div className="h-full bg-[#B4FF39] rounded-full transition-all duration-1000" style={{ width: `${skill.value}%` }}></div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#0A1628] rounded-xl p-4 sm:p-6 border border-gray-700">
            <div className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 uppercase tracking-wider font-semibold">Performance Trend</div>
            <div className="relative h-40 sm:h-48 overflow-x-auto">
              <svg className="w-full h-full min-w-[600px]" viewBox="0 0 800 192" preserveAspectRatio="xMidYMid meet">
                {[0, 25, 50, 75, 100].map((y) => (
                  <line key={y} x1="0" y1={192 - (y * 1.92)} x2="800" y2={192 - (y * 1.92)} stroke="#1A2942" strokeWidth="1" />
                ))}
                <path d="M 50 140 L 150 130 L 250 115 L 350 105 L 450 95 L 550 85 L 650 75 L 750 60"
                  stroke="#B4FF39" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                {[
                  { x: 50, y: 140, score: 78 }, { x: 150, y: 130, score: 82 }, { x: 250, y: 115, score: 86 },
                  { x: 350, y: 105, score: 88 }, { x: 450, y: 95, score: 91 }, { x: 550, y: 85, score: 93 },
                  { x: 650, y: 75, score: 95 }, { x: 750, y: 60, score: 97 }
                ].map((point, idx) => (
                  <g key={idx}>
                    <circle cx={point.x} cy={point.y} r="5" fill="#B4FF39" />
                    <text x={point.x} y={point.y - 12} fill="#B4FF39" fontSize="12" fontFamily="monospace" textAnchor="middle">{point.score}</text>
                  </g>
                ))}
                {['Flight 1', 'Flight 2', 'Flight 3', 'Flight 4', 'Flight 5', 'Flight 6', 'Flight 7', 'Flight 8'].map((label, idx) => (
                  <text key={label} x={50 + (idx * 100)} y="185" fill="#6B7280" fontSize="10" fontFamily="monospace" textAnchor="middle">{label}</text>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}